import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Users, MessageSquare, Settings } from "lucide-react";
import { Link } from "react-router-dom";
import { SystemStatus } from "@/components/admin/dashboard/SystemStatus";

interface DashboardStat {
  icon: React.ElementType;
  label: string;
  value: string | number;
  href: string;
  loading: boolean;
}

export default function SettingsDashboard() {
  // Fetch quotes count
  const { data: quotesCount, isLoading: quotesLoading } = useQuery({
    queryKey: ["quotesCount"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("quotes")
        .select("*", { count: "exact", head: true });
      
      if (error) throw error;
      return count || 0;
    }
  });

  // Fetch categories count
  const { data: categoriesCount, isLoading: categoriesLoading } = useQuery({
    queryKey: ["categoriesCount"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("categories")
        .select("*", { count: "exact", head: true });
      
      if (error) throw error;
      return count || 0;
    }
  });

  // Fetch active subscribers count using the RPC function
  const { data: subscribersCount, isLoading: subscribersLoading } = useQuery({
    queryKey: ["subscribersCount"],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc("get_active_or_subscribed_profiles", undefined, {
          count: "exact"
        });
      
      if (error) throw error;
      return data?.length || 0;
    }
  });

  // Fetch recent messages count (last 24 hours)
  const { data: messagesCount, isLoading: messagesLoading } = useQuery({
    queryKey: ["messagesCount"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("message_logs")
        .select("*", { count: "exact", head: true })
        .gte('sent_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString());
      
      if (error) throw error;
      return count || 0;
    }
  });

  // Fetch recent activity
  const { data: recentActivity, isLoading: activityLoading } = useQuery({
    queryKey: ["recentActivity"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("message_logs")
        .select(`
          id,
          sent_at,
          status,
          phone_number,
          quote_text
        `)
        .order('sent_at', { ascending: false })
        .limit(5);
      
      if (error) throw error;
      return data;
    }
  });

  const stats: DashboardStat[] = [
    {
      icon: FileText,
      label: "Total Quotes",
      value: quotesCount ?? 0,
      href: "/settings/quotes",
      loading: quotesLoading
    },
    {
      icon: Users,
      label: "Active Subscribers",
      value: subscribersCount ?? 0,
      href: "/settings/subscribers",
      loading: subscribersLoading
    },
    {
      icon: MessageSquare,
      label: "Messages (24h)",
      value: messagesCount ?? 0,
      href: "/settings/logs",
      loading: messagesLoading
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <Link to="/verify">
          <Button variant="outline">
            <Settings className="h-4 w-4 mr-2" />
            Admin Login
          </Button>
        </Link>
      </div>

      {/* System Status */}
      <SystemStatus />

      {/* Stats Grid */}
      <div className="grid gap-4">
        <Card>
          <CardHeader>
            <CardTitle>System Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              <div className="flex flex-col items-center">
                <div className="text-2xl font-bold">
                  {quotesLoading ? (
                    <div className="h-8 w-16 animate-pulse bg-muted rounded" />
                  ) : (
                    quotesCount ?? 0
                  )}
                </div>
                <div className="text-sm text-muted-foreground">Quotes</div>
              </div>
              <div className="flex flex-col items-center">
                <div className="text-2xl font-bold">
                  {categoriesLoading ? (
                    <div className="h-8 w-16 animate-pulse bg-muted rounded" />
                  ) : (
                    categoriesCount ?? 0
                  )}
                </div>
                <div className="text-sm text-muted-foreground">Categories</div>
              </div>
              <div className="flex flex-col items-center">
                <div className="text-2xl font-bold">
                  {subscribersLoading ? (
                    <div className="h-8 w-16 animate-pulse bg-muted rounded" />
                  ) : (
                    subscribersCount ?? 0
                  )}
                </div>
                <div className="text-sm text-muted-foreground">Subscribers</div>
              </div>
              <div className="flex flex-col items-center">
                <div className="text-2xl font-bold">
                  {messagesLoading ? (
                    <div className="h-8 w-16 animate-pulse bg-muted rounded" />
                  ) : (
                    messagesCount ?? 0
                  )}
                </div>
                <div className="text-sm text-muted-foreground">Messages (24h)</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Area */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Quick Actions - Left Column */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 grid-cols-2">
            <Link to="/settings/quotes">
              <Button variant="outline" className="w-full">
                <FileText className="h-4 w-4 mr-2" />
                Manage Quotes
              </Button>
            </Link>
            <Link to="/settings/categories">
              <Button variant="outline" className="w-full">
                <FileText className="h-4 w-4 mr-2" />
                Manage Categories
              </Button>
            </Link>
            <Link to="/settings/subscribers">
              <Button variant="outline" className="w-full">
                <Users className="h-4 w-4 mr-2" />
                View Subscribers
              </Button>
            </Link>
            <Link to="/settings/logs">
              <Button variant="outline" className="w-full">
                <MessageSquare className="h-4 w-4 mr-2" />
                Message Logs
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Recent Activity - Right Column */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            {activityLoading ? (
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-8 bg-muted animate-pulse rounded" />
                ))}
              </div>
            ) : recentActivity?.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                No recent activity
              </div>
            ) : (
              <div className="space-y-4">
                {recentActivity?.map((activity) => (
                  <div key={activity.id} className="flex justify-between items-start text-sm">
                    <div>
                      <p className="font-medium truncate max-w-[300px]">
                        {activity.quote_text}
                      </p>
                      <p className="text-muted-foreground">
                        To: {activity.phone_number} • Status: {activity.status}
                      </p>
                    </div>
                    <span className="text-muted-foreground">
                      {new Date(activity.sent_at).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
